import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useBranchStore from 'clientStore/useBranchStore';
import { httpGetBranches, httpPostBranch } from 'data/branch';
import { httpGetCompanies, httpGetCompany } from 'data/company';
import { httpGetMe } from 'data/user';
import { useTranslation } from 'react-i18next';
import notification from 'services/notification';
import {
  extractApiItem,
  extractApiList,
  extractMeUser,
  resolveBranchIdFromUser,
  resolveCompanyFromUser
} from 'utils/companyHelpers';

const CONTEXT_META_KEYS = new Set(['me', 'companies', 'company', 'branches']);

export default function useCompanyBranch() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: meUser } = useQuery({
    queryKey: ['me'],
    queryFn: httpGetMe,
    select: (response) => extractMeUser(response.data)
  });

  const userCompany = resolveCompanyFromUser(meUser);
  const userBranchId = resolveBranchIdFromUser(meUser);
  const { selectedBranchId, setSelectedBranchId, setSelectedCompanyId } = useBranchStore();

  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: () => httpGetCompanies({ page_size: 100 }),
    enabled: !userCompany?.id,
    select: extractApiList
  });

  const companyId = userCompany?.id || companiesQuery.data?.[0]?.id || null;

  const companyQuery = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => httpGetCompany(companyId),
    enabled: Boolean(companyId) && !userCompany?.name,
    select: extractApiItem
  });

  const companyName =
    userCompany?.name || companyQuery.data?.name || companiesQuery.data?.[0]?.name || '';

  const branchesQuery = useQuery({
    queryKey: ['branches', companyId],
    queryFn: () => httpGetBranches({ company: companyId, page_size: 100 }),
    enabled: Boolean(companyId),
    select: extractApiList
  });

  const branches = useMemo(() => branchesQuery.data || [], [branchesQuery.data]);

  useEffect(() => {
    if (companyId != null) {
      setSelectedCompanyId(companyId);
    }
  }, [companyId, setSelectedCompanyId]);

  useEffect(() => {
    if (!branches.length) {
      return;
    }

    const exists = branches.some((branch) => String(branch.id) === String(selectedBranchId));
    if (selectedBranchId && exists) {
      return;
    }

    const preferred =
      branches.find((branch) => String(branch.id) === String(userBranchId)) || branches[0];

    if (preferred?.id != null) {
      setSelectedBranchId(preferred.id);
    }
  }, [branches, selectedBranchId, setSelectedBranchId, userBranchId]);

  const selectedBranch = useMemo(
    () => branches.find((branch) => String(branch.id) === String(selectedBranchId)) || null,
    [branches, selectedBranchId]
  );

  const refetchBranchScopedLists = () => {
    queryClient.invalidateQueries({
      predicate: (query) => !CONTEXT_META_KEYS.has(query.queryKey?.[0])
    });
  };

  const switchBranch = (branch) => {
    if (!branch?.id || String(branch.id) === String(selectedBranchId)) {
      return;
    }

    setSelectedBranchId(branch.id);
    refetchBranchScopedLists();
    notification
      .setMode('success')
      .setMessage(t('Filial almashdi', { name: branch.name || '' }))
      .success();
  };

  const createBranch = useMutation({
    mutationFn: httpPostBranch,
    onSuccess: (response) => {
      const created = extractApiItem(response);
      queryClient.invalidateQueries({ queryKey: ['branches', companyId] });
      if (created?.id != null) {
        setSelectedBranchId(created.id);
        refetchBranchScopedLists();
      }
    }
  });

  return {
    companyId,
    companyName,
    branches,
    selectedBranch,
    selectedBranchId,
    setSelectedBranchId,
    switchBranch,
    createBranch,
    isLoading: companiesQuery.isLoading || companyQuery.isLoading || branchesQuery.isLoading
  };
}
